import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { KidMarker } from '../../../../../../../api/kids/types';
import type {
  KidDocumentForm,
  SetTableTag,
} from '../../../types/KidPdfGeneratorV2.types';
import { getMarkersForPresetTag } from './getMarkersForPresetTag';
import {
  isPresetTableInjectedCell,
  mergeInjectedPresetTableContent,
} from './presetInjectedCells';

/**
 * Manages preset table content: marker-injected cells stay in sync with the API;
 * all other cells are editable and persisted on the form element.
 */
export function usePresetTableContent(
  index: number,
  tag: SetTableTag,
  markers: KidMarker[],
  markerBuiltContent: string[][],
) {
  const { control, setValue, getValues } = useFormContext<KidDocumentForm>();

  const formContent = useWatch({
    name: `elements.${index}.content`,
    control,
  }) as string[][] | undefined;

  const tableMarkers = useMemo(
    () => getMarkersForPresetTag(tag, markers),
    [tag, markers],
  );

  const content = useMemo(
    () =>
      mergeInjectedPresetTableContent(
        tag,
        markerBuiltContent,
        formContent,
      ),
    [tag, markerBuiltContent, formContent],
  );

  useEffect(() => {
    if (content.length === 0) return;

    // Skip if the element was removed/shifted, otherwise a stale index
    // re-creates a ghost element that gets posted to the PDF generator.
    if (getValues(`elements.${index}.tag` as const) !== tag) return;

    const currentContent = getValues(`elements.${index}.content` as const);
    if (JSON.stringify(currentContent) !== JSON.stringify(content)) {
      setValue(`elements.${index}.content` as any, content, {
        shouldDirty: false,
      });
    }

    if (tableMarkers.length > 0) {
      setValue(`elements.${index}.existing_markers` as any, tableMarkers, {
        shouldDirty: false,
      });
    }
  }, [content, getValues, index, setValue, tableMarkers, tag]);

  const updateCell = useCallback(
    (rowIndex: number, colIndex: number, value: string) => {
      if (isPresetTableInjectedCell(tag, rowIndex, colIndex)) return;
      if (getValues(`elements.${index}.tag` as const) !== tag) return;

      const stored =
        (getValues(`elements.${index}.content` as const) as string[][]) ?? [];

      // New blocks start as []; seed from the merged view so early edits land.
      const base =
        stored.length === 0 && content.length > 0
          ? content.map((row) => [...row])
          : stored.map((row) => [...row]);

      const rowCount = Math.max(base.length, rowIndex + 1);
      const colCount = Math.max(
        colIndex + 1,
        ...base.map((row) => row.length),
        0,
      );

      const updatedContent = Array.from({ length: rowCount }, (_, ri) =>
        Array.from({ length: Math.max(base[ri]?.length ?? 0, colCount) }, (_, ci) =>
          ri === rowIndex && ci === colIndex ? value : (base[ri]?.[ci] ?? ''),
        ),
      );

      setValue(`elements.${index}.content` as any, updatedContent, {
        shouldDirty: true,
      });
    },
    [content, getValues, index, setValue, tag],
  );

  const isInjectedCell = useCallback(
    (rowIndex: number, colIndex: number) =>
      isPresetTableInjectedCell(tag, rowIndex, colIndex),
    [tag],
  );

  return {
    content,
    updateCell,
    isInjectedCell,
    tableMarkers,
  };
}
